// Route + trip helpers for LiveTripMap. No dependencies.

export function decodePolyline(str) {
  let i = 0, lat = 0, lng = 0;
  const out = [];
  while (i < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(i++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(i++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    out.push([lat * 1e-5, lng * 1e-5]);
  }
  return out;
}

const strip = (h) => String(h || '')
  .replace(/<div[^>]*>/g, ' · ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

/** Parse `payload.route.mapJson` (a JSON string from the Directions API). */
export function parseMapJson(mapJson) {
  const d = typeof mapJson === 'string' ? JSON.parse(mapJson) : mapJson;
  const [r, ...alts] = d.routes;
  const leg = r.legs[0];
  return {
    copyrights: r.copyrights,
    summary: r.summary,
    start: [leg.start_location.lat, leg.start_location.lng],
    end: [leg.end_location.lat, leg.end_location.lng],
    startAddress: leg.start_address,
    endAddress: leg.end_address,
    distanceText: leg.distance.text,
    distanceValue: leg.distance.value,
    durationText: leg.duration.text,
    durationValue: leg.duration.value,
    trafficText: (leg.duration_in_traffic || leg.duration).text,
    trafficValue: (leg.duration_in_traffic || leg.duration).value,
    points: decodePolyline(r.overview_polyline.points),
    steps: leg.steps.map((s) => ({
      text: strip(s.html_instructions),
      distanceText: s.distance.text,
      at: [s.start_location.lat, s.start_location.lng],
    })),
    alternates: alts.map((a) => ({
      summary: a.summary,
      distanceText: a.legs[0].distance.text,
      durationValue: a.legs[0].duration.value,
      trafficText: (a.legs[0].duration_in_traffic || a.legs[0].duration).text,
      trafficValue: (a.legs[0].duration_in_traffic || a.legs[0].duration).value,
      points: decodePolyline(a.overview_polyline.points),
    })),
  };
}

/** Fall back to plain waypoints when `mapJson` is absent. */
export function routeFromWaypoints(wayPoints, meta = {}) {
  const points = wayPoints.map((w) => [w.latitude ?? w.lat, w.longitude ?? w.lng]);
  return {
    copyrights: '', summary: 'Waypoints',
    start: points[0], end: points[points.length - 1],
    startAddress: '', endAddress: '',
    distanceText: meta.distanceText || '—',
    durationText: meta.durationText || '—',
    trafficText: meta.durationText || '—',
    points, steps: [], alternates: [],
  };
}

/** Route to draw, from the single get-trip-tracking payload. */
export function routeFromPayload(payload) {
  if (payload?.route?.mapJson) return parseMapJson(payload.route.mapJson);
  if (payload?.route?.wayPoints?.length) return routeFromWaypoints(payload.route.wayPoints, payload.eta);
  return null;
}

const R = 6371000;
export function metres(a, b) {
  const dLat = (b[0] - a[0]) * Math.PI / 180, dLng = (b[1] - a[1]) * Math.PI / 180;
  const la = a[0] * Math.PI / 180, lb = b[0] * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la) * Math.cos(lb) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function bearing(a, b) {
  const la = a[0] * Math.PI / 180, lb = b[0] * Math.PI / 180, dl = (b[1] - a[1]) * Math.PI / 180;
  return (Math.atan2(
    Math.sin(dl) * Math.cos(lb),
    Math.cos(la) * Math.sin(lb) - Math.sin(la) * Math.cos(lb) * Math.cos(dl),
  ) * 180 / Math.PI + 360) % 360;
}

export function minutesOf(text) {
  const h = /(\d+)\s*hour/.exec(text || ''), m = /(\d+)\s*min/.exec(text || '');
  return (h ? +h[1] * 60 : 0) + (m ? +m[1] : 0);
}

export const km = (m) => `${(m / 1000).toFixed(1)} km`;

/** Cumulative distances along the route, so progress and trail can be derived. */
export function measure(points) {
  const cum = [0];
  for (let n = 1; n < points.length; n++) cum.push(cum[n - 1] + metres(points[n - 1], points[n]));
  return { cum, total: cum[cum.length - 1] };
}

export function snapToRoute(points, lat, lng) {
  let i = 0, best = Infinity;
  points.forEach((p, n) => { const d = metres(p, [lat, lng]); if (d < best) { best = d; i = n; } });
  return { index: i, offRouteMetres: Math.round(best) };
}

/**
 * Trip progress from the ETA block's metre values:
 *   eta.routeDistanceValue = whole trip, eta.distanceValue = remaining.
 */
export function tripProgress(eta = {}, fallbackTotalM = 0) {
  const total = eta.routeDistanceValue || fallbackTotalM || 0;
  const remain = eta.distanceValue != null ? eta.distanceValue : total;
  const minutes = eta.durationValue || minutesOf(eta.durationText);
  return {
    total,
    remain,
    covered: Math.max(0, total - remain),
    pct: total ? Math.min(100, Math.max(0, Math.round((1 - remain / total) * 100))) : 0,
    minutes,
    message: eta.trackingMessage || (minutes ? `${eta.isBoarded ? 'Drop' : 'Pickup'} in ${minutes} mins` : '—'),
  };
}

/**
 * The API spells the GPS block "vehicelGps"; earlier payloads used
 * vehicleGeoLocation. Normalise either, and fall back to driverInfo lat/lng.
 */
export function gpsFrom(payload) {
  const g = payload?.vehicelGps || payload?.vehicleGps || payload?.vehicleGeoLocation;
  if (g?.latitude != null) {
    return {
      latitude: +g.latitude, longitude: +g.longitude,
      speed: g.speed != null ? +g.speed : null,
      bearing: g.bearing != null ? +g.bearing : null,
      altitude: g.altitude != null ? +g.altitude : null,
      gpsTime: g.gpsTime || '',
      address: g.address || '',
    };
  }
  const d = payload?.driverInfo;
  return d ? {
    latitude: +d.vehicleLatitude, longitude: +d.vehicleLongitude,
    speed: null, bearing: null, altitude: null, gpsTime: '', address: '',
  } : null;
}

/** The passenger using this screen — isCurrentPassenger, at any list position. */
export function currentPassenger(passengers = []) {
  return passengers.find((p) => p.isCurrentPassenger)
    || passengers.find((p) => !p.isBoarded)
    || passengers[0]
    || null;
}

/** Distance still to run along the route from the vehicle's snapped position. */
export function remainingAlong(points, cum, lat, lng) {
  const { index, offRouteMetres } = snapToRoute(points, lat, lng);
  return { index, offRouteMetres, remain: Math.max(0, cum[cum.length - 1] - cum[index]) };
}

/**
 * Drives the two calls your app already owns. Pass the functions from
 * src/apis/creweasyMobileApi.js — this file never builds a URL, reads a token,
 * or knows an endpoint name.
 *
 *   getTracking() → get-trip-tracking response (called once, and on replot)
 *   getGps()      → get-vehice-gps-loaction response (called every gpsIntervalMs)
 *
 * Either may resolve the full envelope ({ success, payload }) or the payload
 * itself; both are unwrapped. Returns { stop, replot }.
 */
const unwrap = (res) => (res && res.payload !== undefined ? res.payload : res);

export function connectTrip({ getTracking, getGps, gpsIntervalMs = 10000,
  onTracking, onGps, onError }) {
  let stopped = false, timer = null, replotting = false;

  const gpsTick = () => Promise.resolve()
    .then(getGps)
    .then((res) => { const p = unwrap(res); if (!stopped && p && onGps) onGps(p); })
    .catch((err) => { if (!stopped && onError) onError(err); });

  Promise.resolve()
    .then(getTracking)
    .then((res) => {
      if (stopped) return;
      const p = unwrap(res);
      if (p && onTracking) onTracking(p);
      if (!getGps) return;
      gpsTick();
      timer = setInterval(gpsTick, gpsIntervalMs);
    })
    .catch((err) => { if (!stopped && onError) onError(err); });

  return {
    stop() { stopped = true; if (timer) clearInterval(timer); },
    replot() {
      if (replotting || stopped || !getTracking) return Promise.resolve(false);
      replotting = true;
      return Promise.resolve()
        .then(getTracking)
        .then((res) => { const p = unwrap(res); if (p && !stopped && onTracking) onTracking(p); return true; })
        .catch((err) => { if (onError) onError(err); return false; })
        .finally(() => { replotting = false; });
    },
  };
}
